import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import { getAllOrganizer,getEntitlements,updateUser } from "api";


const OrganizerEntitlements = () => {
  document.title = "Organizer Permissions | EventoX";

  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState([]);
  const [selectedOrganizers, setSelectedOrganizers] = useState([]);
  const [selectedEntitlements, setSelectedEntitlements] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);

  const toggleEdit = () => setEditModal(!editModal);

  useEffect(() => {
    const fetch = async () => {
      try {
        const orgs = await getAllOrganizer();
        const raw = await getEntitlements();
        const ents = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
        ? raw
        : [];
        const formatted = ents.map((e) => ({
          value: e.entitlementKey,
          label: e.entitlementName,
        }));
        setEntitlements(formatted);
        setOrganizers(Array.isArray(orgs) ? orgs.map((o) => ({
          ...o,entitlements: Array.isArray(o.entitlements) ? o.entitlements : [],
        })) : []);
      } catch (err) {
        toast.error("Failed to load organizers");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const organizerOptions = organizers.map((o) => ({
    value: o.organizerId,
    label: o.name,
  }));

  const handleBulkAssign = async () => {
    if (!selectedOrganizers.length) {
      toast.error("Select at least one organizer");
      return;
    }

    setIsSaving(true);

    try {
      console.log("Selected Entitlement Keys:", selectedEntitlements);
      await Promise.all(
        selectedOrganizers.map((org) =>
          updateUser(org.value, { entitlements: selectedEntitlements })
        )
      );
      const updated = organizers.map((org) => {
        const match = selectedOrganizers.find((s) => s.value === org.organizerId);
        return match
          ? { ...org, entitlements: selectedEntitlements }
          : org;
      });
      setOrganizers(updated);
      toast.success("Permissions updated");

      setSelectedOrganizers([]);
      setSelectedEntitlements([]);
    } catch (err) {
      toast.error("Failed to update permissions");
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (org) => {
    setEditOrg(org);
    setSelectedEntitlements(
      entitlements
        .filter((e) => org.entitlements.includes(e.value))
        .map((e) => e.value)
    );
    
    toggleEdit();
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
  
    try {
      console.log("Selected Entitlement Keys:", selectedEntitlements);
  
      await updateUser(editOrg.organizerId, {
        entitlements: selectedEntitlements,
      });
  
      const updated = organizers.map((org) =>
        org.organizerId === editOrg.organizerId
          ? { ...org, entitlements: selectedEntitlements }
          : org
      );
  
      setOrganizers(updated);
      toast.success("Updated successfully");
      toggleEdit();
    } catch (err) {
      toast.error("Failed to update");
    } finally {
      setIsSaving(false);
    }
  };
  

  const columns = useMemo(
    () => [
      {
        header: "profile",
        accessorKey: "profilePic",
        enableColumnFilter: false,   
        cell: (cell) => {
          const img = cell.getValue();
          const row = cell.row.original;

          return img ? (
            <img src={img} className="rounded-circle avatar-xs" alt="" />
          ) : (
            <div className="avatar-xs">
              <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                {row.name.charAt(0)}
              </span>
            </div>
          );
        },
      },
      {
        header: "Organizer",
        accessorKey: "name",
        cell: (cell) => (
          <div style={{ maxWidth: "200px" }}>
            <h6
              className="mb-0 text-truncate"
              style={{
                maxWidth: "250px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
              title={cell.getValue()}  
                >
                  {cell.getValue()}
            </h6>
            <small className="text-muted d-block text-truncate">
              {cell.row.original.email}
            </small>

            <small className="text-muted d-block text-truncate">
              {cell.row.original.phone}
            </small>

            <small className="text-muted d-block text-truncate">
              {cell.row.original.Country?.countryName}
            </small>
          </div>
        ),
      },

      // { header: "Phone", accessorKey: "phone" },
      // { header: "Country", accessorKey: "Country.countryName" },
   
      {
        header: "Permissions",
        accessorKey: "entitlements",
        cell: (cell) => {
          const perms = cell.getValue() || [];

          if (!perms.length) return <small className="text-muted">No permissions</small>;

          return perms.map((p) => {
            const opt = entitlements.find((o) => o.value === p);
            return (
              <span key={p} className="badge bg-info font-size-11 m-1">
                {opt ? opt.label : p}
              </span>
            );
          });
        },
      },
   {
        header: "Status",
        accessorKey: "status",
        size: 90, 
        maxSize: 90,
        minSize: 70,

        cell: (cell) => (
          <span
            className={`badge ${
              cell.getValue() === "active"
                ? "badge-soft-success"
                : "badge-soft-secondary"
            }`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const org = cellProps.row.original;
          return (
            <Button color="primary" size="sm" onClick={() => openEditModal(org)}>
              <i className="mdi mdi-shield-edit-outline"></i> Edit
            </Button>
          );
        },
      },
    ],
    [entitlements]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Admin" breadcrumbItem="Organizer Permissions" />

        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
          To enable <strong>Ask Ev</strong>, create entitlement key <code>ask_ev</code> under Manage Entitlements,
          then assign it to organizers here. Organizers without it cannot use Ask Ev.
        </p>

        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col lg={6}>
                <FormGroup>
                  <Label>Select Organizers</Label>
                  <Select
                    isMulti
                    value={selectedOrganizers}
                    onChange={(opts) => setSelectedOrganizers(opts || [])}
                    options={organizerOptions}
                    placeholder="Select organizers"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                        menu: provided => ({ ...provided, zIndex: 9999 })
                      }}
                  />
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup>
                  <Label>Select Permissions</Label>
                  <Select
                    isMulti
                    value={entitlements.filter((p) =>
                      selectedEntitlements.includes(p.value)
                    )}
                    onChange={(opts) =>
                      setSelectedEntitlements(opts ? opts.map((o) => o.value) : [])
                    }
                    options={entitlements}
                    placeholder="Select permissions"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                        menu: provided => ({ ...provided, zIndex: 9999 })
                      }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="text-end">
              <Button color="success" onClick={handleBulkAssign} disabled={isSaving}>
                {isSaving ? <Spinner size="sm" /> : "Assign Permissions"}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {loading ? (
              <div className="py-5 text-center">
                <Spinner />
              </div>
            ) : (
              <Row>
                <Col lg="12">
        <Card>
          <CardBody>
            {loading ? (
              <div className="py-5 text-center">
                <Spinner />
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={organizers}
                          isGlobalFilter={true}
                          SearchPlaceholder="Search..."
                          isPagination={true}
                          tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                          theadClass="table-light"
                          paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

            )}
          </CardBody>
        </Card>

        {/* EDIT MODAL */}
        <Modal isOpen={editModal} toggle={toggleEdit}>
          <ModalHeader toggle={toggleEdit}>Edit Permissions</ModalHeader>
          <ModalBody>
            {editOrg && (
              <>
                <div className="d-flex align-items-center mb-3">
                  {editOrg.profilePic ? (
                    <img
                      src={editOrg.profilePic}
                      alt=""
                      className="rounded-circle avatar-xs me-3"
                    />
                  ) : (
                    <div className="avatar-xs me-3">
                      <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                        {editOrg.name.charAt(0)}
                      </span>
                    </div>
                  )}

                  <div>
                    <h6 className="mb-0">{editOrg.name}</h6>
                    <small className="text-muted">{editOrg.email}</small>
                  </div>
                </div>

                <FormGroup>
                  <Label>Permissions</Label>
                  <Select
                    isMulti
                    value={entitlements.filter((p) =>
                      selectedEntitlements.includes(p.value)
                    )}
                    onChange={(opts) =>
                      setSelectedEntitlements(opts ? opts.map((o) => o.value) : [])
                    }
                    options={entitlements}
                    placeholder="Select permissions"
                    isClearable
                  />
                </FormGroup>

                <div className="text-end">
                  <Button
                    color="success"
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                  >
                    {isSaving ? <Spinner size="sm" /> : "Save"}
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default OrganizerEntitlements;
